import { useMemo } from 'react';
import { Button, Card, Column, Divider, DropdownMenu, IconButton, Row, Typography, useOverlayContext } from 'seagull';
import { ArrowRight, Download, MoreVertical, UserAddLiner24 } from 'seagull/icons';
import { getAgreementPdfUrl } from "../../../../business-logic/relationshipLogic";
import InviteOrEditMemberModal from "../../../../components/InviteOrEditMemberModal";
import RecipientDetails from "../../../../components/RecipientDetails/RecipientDetails";
import { useIoContext, useStore } from "../../../../contexts";
import { relationshipModel } from "../../../../models";
import { fromDateStampObject } from "../../../../models/time";
import { selectors } from "../../../../store/state-selectors";
import { formatLongMonthUSDate } from "../../../../utils";
import { getLimitedTimeDiscountOfferCalloutText } from "../../WelcomeStep/welcomeStepUtils";
import { GetCoverPageTID } from "./CoverPage";
import styles from "./CoverPageMobile.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const CoverPageMobile = _ref => {
  var _relationship$signato;
  let {
    testId,
    relationship,
    onGetStarted,
    isPreview
  } = _ref;
  const {
    CoverPageTestIds
  } = useMemo(() => GetCoverPageTID(testId), [testId]);
  const vendorName = relationship.vendor.name;
  const clientName = relationship.customer.name;
  const vendorLogoUrl = relationshipModel.vendorLogoUrl({
    vendor: relationship.vendor
  });
  const {
    showModal
  } = useOverlayContext();
  const {
    openNewTab
  } = useIoContext();
  const pdfHost = useStore(selectors.pdfHost);
  const expirationDate = relationship.expirationDate ? fromDateStampObject(relationship.expirationDate) : null;
  const discountCalloutText = getLimitedTimeDiscountOfferCalloutText(relationship);
  const hasExpirationInfo = expirationDate || discountCalloutText;
  const isDisabled = Boolean(relationship.isSample || isPreview);
  const onDownloadPdf = () => {
    openNewTab(getAgreementPdfUrl(relationshipModel.id(relationship), pdfHost));
  };
  const openInviteMemberModal = () => {
    showModal({
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(InviteOrEditMemberModal, {
          isResponsive: true,
          onClose: dismissModal
        });
      }
    });
  };
  return /*#__PURE__*/_jsx(Card, {
    padding: "m",
    className: styles.card,
    "data-testid": CoverPageTestIds.Root(),
    children: /*#__PURE__*/_jsxs(Column, {
      className: styles.container,
      children: [/*#__PURE__*/_jsxs(Row, {
        className: styles.header,
        justifyContent: "space-between",
        alignItems: "start",
        children: [vendorLogoUrl ? /*#__PURE__*/_jsx("img", {
          "data-testid": CoverPageTestIds.VendorLogo(),
          src: vendorLogoUrl,
          alt: vendorName,
          className: styles.logo
        }) : /*#__PURE__*/_jsx(Typography, {
          variant: "h5",
          weight: "bold",
          children: vendorName
        }), /*#__PURE__*/_jsxs(DropdownMenu, {
          target: /*#__PURE__*/_jsx(IconButton, {
            disabled: isDisabled,
            children: /*#__PURE__*/_jsx(MoreVertical, {})
          }),
          children: [/*#__PURE__*/_jsx(DropdownMenu.Item, {
            iconStart: /*#__PURE__*/_jsx(Download, {}),
            onClick: onDownloadPdf,
            children: "Download PDF"
          }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
            iconStart: /*#__PURE__*/_jsx(UserAddLiner24, {}),
            onClick: openInviteMemberModal,
            children: "Invite a team member"
          })]
        })]
      }), /*#__PURE__*/_jsxs(Column, {
        className: styles.content,
        children: [/*#__PURE__*/_jsxs(Column, {
          gap: 8,
          children: [/*#__PURE__*/_jsx(Typography, {
            "data-testid": CoverPageTestIds.ProposalTitle(),
            variant: "h2",
            weight: "bold",
            fontFamily: "hero",
            children: "Proposal for review"
          }), /*#__PURE__*/_jsxs(Typography, {
            "data-testid": CoverPageTestIds.ProposalPreparedFor(),
            variant: "h5",
            weight: "normal",
            fontFamily: "hero",
            children: ["Prepared for ", clientName]
          }), /*#__PURE__*/_jsxs(Typography, {
            "data-testid": CoverPageTestIds.PropsalDescription(),
            children: [vendorName, " has sent you this proposal and is requesting that you sign and accept it."]
          })]
        }), /*#__PURE__*/_jsx(Divider, {
          margin: 16
        }), /*#__PURE__*/_jsxs(Column, {
          gap: 8,
          children: [/*#__PURE__*/_jsx(Typography, {
            "data-testid": CoverPageTestIds.RecipientsTitle(),
            variant: "paragraph2",
            skin: "secondary",
            children: "Recipients"
          }), /*#__PURE__*/_jsx(RecipientDetails, {
            className: styles.recipient,
            firstName: relationship.buyer.firstName,
            lastName: relationship.buyer.lastName,
            badge: "required",
            requiredBadgeSkin: "primary"
          }), (_relationship$signato = relationship.signatories) === null || _relationship$signato === void 0 ? void 0 : _relationship$signato.map(signatory => /*#__PURE__*/_jsx(RecipientDetails, {
            testid: CoverPageTestIds.Recipient(signatory.id),
            className: styles.recipient,
            firstName: signatory.firstName,
            lastName: signatory.lastName,
            badge: relationshipModel.hasSignatorySigned(relationship, signatory.id) ? 'signed' : 'required',
            requiredBadgeSkin: "primary"
          }, signatory.id)), relationship.reviewers.map(reviewer => /*#__PURE__*/_jsx(RecipientDetails, {
            testid: CoverPageTestIds.Reviewer(reviewer.id),
            className: styles.recipient,
            firstName: reviewer.firstName,
            lastName: reviewer.lastName
          }, reviewer.id))]
        })]
      }), /*#__PURE__*/_jsxs(Column, {
        className: styles.footer,
        gap: 16,
        children: [hasExpirationInfo && /*#__PURE__*/_jsxs(Typography, {
          variant: "paragraph2",
          "data-testid": CoverPageTestIds.ProposalExpirationDate(),
          children: [expirationDate && "This proposal is valid until ".concat(formatLongMonthUSDate(expirationDate), ". "), discountCalloutText]
        }), /*#__PURE__*/_jsx(Button, {
          "data-testid": CoverPageTestIds.GetStarted(),
          suffixIcon: /*#__PURE__*/_jsx(ArrowRight, {}),
          onClick: onGetStarted,
          className: styles.button,
          children: "Get started"
        })]
      })]
    })
  });
};